import React, { FC, useEffect } from 'react';
import classNames from 'classnames/bind';

import { connect } from 'react-redux';
import { InView } from 'react-intersection-observer';
import Text from '../Text';

import styles from './index.pcss';
import ProgressLabel from '../ProgressLabel';
import { gtmEventPush } from '../../../modules/ga/GTMdatalayer';
import { getSubratingFlag } from '../../selectors/featureFlagsSelector';

interface CustomerOpinionProps {
  subRating: any;
  titleD: any;
  isFleek?: boolean;
}

const CustomerOpinion: FC<CustomerOpinionProps> = (props: CustomerOpinionProps) => {
  const {
    noReviews,
    subRating,
    titleD,
    isDesktop,
    productCode,
    productName,
    product_id,
    product_brick,
    aggregateRating,
    customerWords,
    ratingQuestionsMapping,
    fromViewAllDesktop = false,
    isFleek = false
  } = props;
  const cx = classNames.bind(styles);
  const subrateGAView = ({ isInView }) => {
    isInView &&
      subRating &&
      subRating.map(item => {
        let maxValue = item?.attributeRatings.reduce((acc, value) => {
          const prevVal = acc?.percentageRating.replace('%', '').trim();
          const nextVal = value?.percentageRating.replace('%', '').trim();
          return prevVal >= nextVal ? acc : value;
        });
        const question = item?.productAttribute;
        gtmEventPush({
          event: 'reviews_rating_interactions',
          event_category: 'reviews & ratings interactions',
          event_action: `Sub Rating | ${question}`,
          event_label: maxValue?.text,
          page_type: 'product page',
          product_name: productName,
          product_id: productCode,
          product_brick
        });
      });
  };

  const subRatingOrder = {
    'How was the Product fit?': ['Perfect', 'Loose', 'Tight', 'Too loose', 'Too Tight'],
    'How was the Product Quality?': ['Excellent', 'Very Good', 'Average', 'Bad', 'Very Bad'],
    'How is the value for money?': ['Excellent', 'Very Good', 'Average', 'Bad', 'Very Bad']
  };

  return (
    <InView
      as="div"
      key={productCode}
      className={`${productCode}`}
      threshold={0.5}
      triggerOnce={true}
      onChange={(inView, entry) => subrateGAView({ isInView: inView })}
    >
      <div
        className={cx(
          'customer-opnion',
          isDesktop ? 'desk-custmer-opinion' : 'mob-custmer-opinion',
          isFleek ? 'fleek-mob-custmer-opinion' : ''
        )}
      >
        <div className={cx('title')}>
          <Text
            variant={titleD.textV}
            weight={titleD.weightV}
            color={isFleek ? 'White' : titleD.colorV}
            role='heading'
            ariaLevel={4}
          >
            Customer Opinion
          </Text>
        </div>
        <div className={cx('customer-opnion-details')}>
          <ul
            className={cx(
              'customer-opnion-list',
              !noReviews ? 'customer-opnion-list-vertical' : ''
            )}
          >
            {subRating &&
              subRating.map((subRatingValue, index) => {
                let maxValue = subRatingValue?.attributeRatings.reduce((acc, value) => {
                  const prevVal = +(acc?.percentageRating?.replace('%', '').trim() ?? 0);
                  const nextVal = +(value?.percentageRating?.replace('%', '').trim() ?? 0);
                  return prevVal >= nextVal ? acc : value;
                }, {});

                const maxValueSameForTwo = subRatingValue?.attributeRatings.filter(
                  el =>
                    +(el?.percentageRating?.replace('%', '').trim() ?? 0) ===
                    +(maxValue?.percentageRating?.replace('%', '').trim() ?? 0)
                );

                if (maxValueSameForTwo.length > 1) {
                  maxValue = (subRatingOrder[subRatingValue?.productAttribute ?? ''] ?? [])
                    .map(el => maxValueSameForTwo.find(cur => cur.text === el))
                    .filter(el => el)?.[0];
                }

                const details = subRatingValue?.attributeRatings.map((val1, i) => {
                  return (
                    <li className={cx('progress-list')} key={i} aria-labelledby={`mob-progress-${i} ratingspercentage${i}`}>
                      <span className={cx('Excellent')}>
                        <ProgressLabel
                          height={isDesktop ? '8px' : '5px'}
                          progress={val1.percentageRating}
                          bgcolor={isDesktop ? '#d5a248' : '#1a4e7c'}
                          prgPercentText={val1.text}
                          textFontSize={isDesktop ? '12px' : '10px'}
                          isHighest={maxValue?.text === val1.text}
                          isDesktop={isDesktop}
                          customerWords={customerWords}
                          isFleek={isFleek}
                          indexNum={i}
                        />
                      </span>
                    </li>
                  );
                });
                const shortenedValue = ratingQuestionsMapping?.filter(
                  product =>
                    String(product.question).toLowerCase() ===
                    String(subRatingValue.productAttribute).toLowerCase()
                );

                return (
                  <li
                    className={cx(fromViewAllDesktop ? 'sub-rating-div-viewAll' : 'sub-rating-div')}
                    key={index}
                  >
                    <p className={cx('customer-feedback')} role='heading' aria-level={5}>
                      {shortenedValue
                        ? shortenedValue[0]?.question
                        : subRatingValue.productAttribute}
                    </p>
                    <ul>{details}</ul>
                  </li>
                );
              })}
          </ul>
        </div>
      </div>
    </InView>
  );
};
const mapStateToProps = state => {
  const ratingQuestionsMapping = getSubratingFlag(state);
  return {
    ratingQuestionsMapping
  };
};
const connector = connect(mapStateToProps);
export default connector(CustomerOpinion);
